import { getSpeciesSlug } from "./speciesSlugUtils";
import { Species } from "@/data/species/types/Species";
import { filterCatalogSpecies } from "@/utils/speciesForagePrimary";
import { enrichSpeciesActivityTags } from "./species/mergeSpeciesDetail";
import {
  NATURAL_MOREL_SPECIES_NAME,
  NATURAL_MOREL_SPECIES_SLUG,
} from "@/constants/burnMorelForecast";

const SPECIES_API_TIMEOUT_MS = 8000;
const SPECIES_VIEW_FULL = "full";
const SPECIES_VIEW_SUMMARY = "summary";

const fetchSpeciesFromApi = async (
  view = SPECIES_VIEW_FULL,
  options: { slug?: string; signal?: AbortSignal } = {},
) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), SPECIES_API_TIMEOUT_MS);
  options.signal?.addEventListener("abort", () => controller.abort());
  const params = new URLSearchParams();
  if (view === SPECIES_VIEW_SUMMARY) params.set("view", "summary");
  if (options.slug) params.set("slug", options.slug);
  const query = params.toString() ? `?${params.toString()}` : "";

  const response = await fetch(`/api/species${query}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    signal: controller.signal,
  });

  clearTimeout(timeout);

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const details = body?.details || response.statusText;
    throw new Error(`Species API failed: ${response.status} ${details}`);
  }

  const data = await response.json();
  if (!Array.isArray(data)) {
    throw new Error("Species API returned an invalid payload");
  }

  return data;
};

function applyNaturalMorelDisplayName(entry: Species): Species {
  const slug = entry.slug?.trim().toLowerCase();
  if (slug !== NATURAL_MOREL_SPECIES_SLUG) return entry;
  if (entry.name === NATURAL_MOREL_SPECIES_NAME) return entry;
  return { ...entry, name: NATURAL_MOREL_SPECIES_NAME };
}

/** Legacy CMS rows that still need taxonomy fixes until patched in Sanity. */
function applyCatalogTaxonomyFixes(entry: Species): Species {
  const slug = entry.slug?.trim().toLowerCase();
  if (slug === "alectoris-chukar" && entry.category === "other") {
    return { ...entry, category: "game" };
  }
  return entry;
}

function normalizeSpeciesFromApi(species: Record<string, unknown>): Species {
  const sanitySlug = species.slug && String(species.slug).trim();
  const fallbackSlug = getSpeciesSlug(
    (species.scientificName && String(species.scientificName)) ||
      (species.name && String(species.name)) ||
      "",
  );
  const base = {
    ...species,
    slug: sanitySlug || fallbackSlug,
  } as Species;
  return enrichSpeciesActivityTags(
    applyNaturalMorelDisplayName(applyCatalogTaxonomyFixes(base)),
  );
}

/**
 * Get all species from the API (Sanity). No local fallback.
 */
export const getAllSpecies = async (options: { summary?: boolean } = {}) => {
  const apiSpecies = await fetchSpeciesFromApi(
    options.summary ? SPECIES_VIEW_SUMMARY : SPECIES_VIEW_FULL,
  );
  if (apiSpecies.length === 0) {
    throw new Error("Species API returned an empty list");
  }
  const catalog = filterCatalogSpecies(
    apiSpecies.map((species) => normalizeSpeciesFromApi(species)),
  );
  if (catalog.length === 0) {
    throw new Error("Species API returned an empty list");
  }
  return catalog;
};

/**
 * One catalog species (full payload) by slug, without loading the whole
 * catalog. Used by the field-guide detail route so it can render before the
 * 140-species list arrives (WN-223). Null when the slug is not in the catalog.
 */
export const getCatalogSpeciesBySlug = async (
  slug: string,
  options: { signal?: AbortSignal } = {},
): Promise<Species | null> => {
  const apiSpecies = await fetchSpeciesFromApi(SPECIES_VIEW_FULL, {
    slug: slug.trim().toLowerCase(),
    signal: options.signal,
  });
  const catalog = filterCatalogSpecies(
    apiSpecies.map((species) => normalizeSpeciesFromApi(species)),
  );
  return catalog[0] ?? null;
};

/**
 * Find species by name (case-insensitive)
 */
export const findSpeciesByName = async (name: string) => {
  const species = await getAllSpecies();
  return species.find((s) => s.name.toLowerCase() === name.toLowerCase());
};







const MONTH_MAP: Record<string, number> = {
  january: 1,
  jan: 1,
  february: 2,
  feb: 2,
  march: 3,
  mar: 3,
  april: 4,
  apr: 4,
  may: 5,
  june: 6,
  jun: 6,
  july: 7,
  jul: 7,
  august: 8,
  aug: 8,
  september: 9,
  sep: 9,
  sept: 9,
  october: 10,
  oct: 10,
  november: 11,
  nov: 11,
  december: 12,
  dec: 12,
};

function toMonthNumber(value: unknown): number | null {
  if (typeof value === "number" && value >= 1 && value <= 12) return value;
  if (value == null) return null;
  const key = String(value).toLowerCase().trim();
  if (!key) return null;
  const n = MONTH_MAP[key];
  return n != null ? n : null;
}

/** "Year-round", "year round", "All year" (any case) as a season start/end. */
function isYearRoundLabel(value: unknown): boolean {
  if (value == null) return false;
  const key = String(value).toLowerCase().trim().replace(/[\s-]+/g, " ");
  return key === "year round" || key === "all year";
}

function isYearRoundSeason(season: SpeciesSeason): boolean {
  return isYearRoundLabel(season.start) || isYearRoundLabel(season.end);
}

const MONTH_ABBREV = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

function abbreviateMonthLabel(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const monthNumber = toMonthNumber(trimmed);
  if (monthNumber != null) return MONTH_ABBREV[monthNumber - 1];
  return trimmed;
}

/** Compact harvest season for species cards (e.g. "Jun - Sep"). */
export function formatSpeciesSeasonLabel(
  season?: SpeciesSeason | null,
): string {
  if (season && isYearRoundSeason(season)) return "Year-round";
  const startRaw = String(season?.start ?? "").trim();
  const endRaw = String(season?.end ?? "").trim();
  if (!startRaw && !endRaw) return "-";
  const start = startRaw ? abbreviateMonthLabel(startRaw) : "";
  const end = endRaw ? abbreviateMonthLabel(endRaw) : "";
  if (start && end) return `${start} - ${end}`;
  return start || end || "-";
}

/**
 * Field Guide lists and search suggestions skip entries that are routable by slug for a dedicated
 * map pane but should not appear as browsable species (e.g. the "Burn Morels" forecast pane).
 */
export function isFieldGuideVisible(species: {
  hiddenFromFieldGuide?: boolean;
}): boolean {
  return !species?.hiddenFromFieldGuide;
}

interface SpeciesSeason {
  start?: string | number;
  end?: string | number;
  startMonth?: number;
  endMonth?: number;
}

function hashSeed(seed: string): () => number {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return () => {
    hash = (hash * 1664525 + 1013904223) >>> 0;
    return hash / 0x100000000;
  };
}

function shuffleStable<T>(items: T[], seed: string): T[] {
  const copy = [...items];
  const random = hashSeed(seed);
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/**
 * Field-guide browse order: in season first, coming soon last.
 * Within each group the mix is shuffled (stable for a given seed) so the
 * grid is not a straight A–Z list.
 */
export function sortFieldGuideBrowse<T extends {
  comingSoon?: boolean;
  season?: SpeciesSeason | null;
  slug?: string;
  name?: string;
}>(species: T[], seed?: string): T[] {
  const day = seed ?? new Date().toISOString().slice(0, 10);
  const inSeason: T[] = [];
  const rest: T[] = [];
  const soon: T[] = [];
  for (const entry of species) {
    if (entry.comingSoon) soon.push(entry);
    else if (isInSeason(entry)) inSeason.push(entry);
    else rest.push(entry);
  }
  const key = (entry: T) => entry.slug || entry.name || '';
  return [
    ...shuffleStable(inSeason, `${day}:in:${inSeason.map(key).join('|')}`),
    ...shuffleStable(rest, `${day}:out:${rest.map(key).join('|')}`),
    ...shuffleStable(soon, `${day}:soon:${soon.map(key).join('|')}`),
  ];
}

/**
 * True when species has a valid harvest season and today falls inside that window
 * (e.g. July 1–September 30 for summer berries). A start after the end wraps over
 * the new year (Oct–Apr). "Year-round" → always true. No season in Sanity → false.
 */
export function isInSeason(species: {
  season?: SpeciesSeason | null;
}): boolean {
  const season = species?.season;
  if (!season) return false;
  if (isYearRoundSeason(season)) return true;

  const startMonth =
    season.startMonth != null &&
    season.startMonth >= 1 &&
    season.startMonth <= 12
      ? season.startMonth
      : toMonthNumber(season.start);
  const endMonth =
    season.endMonth != null && season.endMonth >= 1 && season.endMonth <= 12
      ? season.endMonth
      : toMonthNumber(season.end);

  if (startMonth == null || endMonth == null) return false;

  const hasSeasonSet =
    season.start != null &&
    String(season.start).trim() !== "" &&
    season.end != null &&
    String(season.end).trim() !== "";
  if (!hasSeasonSet) return false;

  // Whole-month windows: compare month numbers so the entire last day counts.
  const month = new Date().getMonth() + 1;
  if (startMonth <= endMonth) {
    return month >= startMonth && month <= endMonth;
  }
  // start > end is a season that wraps over the new year (e.g. Oct–Apr).
  return month >= startMonth || month <= endMonth;
}

/**
 * Get species list formatted for UI components.
 * Includes all fields needed for map layers and detail pages.
 */
export const getSpeciesListForUI = async () => {
  const species = await getAllSpecies();
  return species.map((s) => ({
    commonName: s.name,
    scientificName: s.scientificName,
    taxonId: s.taxonId,
    taxonIds: [s.taxonId],
    slug: s.slug,
    description: s.description,
    habitat: s.habitat,
    season: s.season,
    imageUrl: s.imageUrl,
    forestCodes: s.forestCodes,
    mapboxTilesetUrl: s.mapboxTilesetUrl,
    mapboxSourceLayer: s.mapboxSourceLayer,
    requirePremiumTiles: s.requirePremiumTiles,
  }));
};
